import { Box, VStack } from "@chakra-ui/react"
import Alert from "@pathwright/ui/src/components/alert/Alert"
import useDocumentEventDispatcher from "@pathwright/web/src/modules/lib/useDocumentEventDispatcher"
import classnames from "classnames"
import { ConnectStores } from "lib/core/store"
import { DESIGN } from "path/constants"
import useInterceptedLinks from "../path/useInterceptedLinks"
import StepContent from "./StepContent"
import StepDiscussion from "./StepDiscussion"
import StepDiscussionFeed from "./StepDiscussionFeed"
import StepDownloads from "./StepDownloads"
import StepHeader from "./StepHeader"
import StepCompletionContainer from "./completion/StepCompletionContainer"
import {
  RegisterLock,
  StepPasswordLock,
  StepPermissionLock,
  StepStartLock
} from "./lock"
import "./styles/Step.css"

const Step = ({
  step,
  mode,
  content,
  content_error,
  reloadStep,
  onContentSaved,
  isActive,
  isVisible,
  isEditingMode,
  isEditingStep,
  isLicensed,
  isStartLocked,
  isPasswordLocked,
  isPermissionLocked,
  isContentVisible,
  isCompletionVisible,
  isDiscussionQuestionsVisible,
  isDiscussionPromptsVisible,
  canPostDiscussionQuestion,
  canPostQuestion,
  nonPassiveStepStarts,
  isRegisterLocked,
  handleBlocksCompletion,
  handleLegacyContentCompletion,
  isCompletionDisabled,
  completionToken,
  hasLegacyContent
}) => {
  const canRenderContent = isVisible && isContentVisible

  const setInterceptLinksNode = useInterceptedLinks()
  const setDispatcherNode = useDocumentEventDispatcher(
    mode != DESIGN ? "step:content:view:rendered" : "step:content:edit:rendered"
  )

  const className = classnames({
    Step: true,
    "Step--card": true,
    "Step--is-active": isActive, // for printing active step
    "Step--is-visible": isVisible
  })

  // avoid rendering the whole Step if the step isn't visible
  if (!isVisible) {
    return <div className={className}></div>
  }

  return (
    <VStack
      className={className}
      ref={(ref) => {
        setDispatcherNode(ref)
        setInterceptLinksNode(ref)
      }}
      spacing={4}
      w="100%"
    >
      <Box className="Step__ContentWrap" w="100%">
        <StepHeader step={step} mode={mode} isEditingStep={isEditingStep} />
        {isRegisterLocked ? (
          <RegisterLock />
        ) : isStartLocked ? (
          <StepStartLock
            step={step}
            nonPassiveStepStarts={nonPassiveStepStarts}
            onReload={reloadStep}
          />
        ) : isPasswordLocked ? (
          <StepPasswordLock step={step} />
        ) : isPermissionLocked ? (
          <StepPermissionLock />
        ) : content_error ? (
          <Alert error={content_error} />
        ) : canRenderContent ? (
          <StepContent
            key={`${step.id}-content`}
            step={step}
            content={content}
            isEditing={isEditingMode}
            isLicensed={isLicensed}
            onContentSaved={onContentSaved}
            handleBlocksCompletion={handleBlocksCompletion}
            handleLegacyContentCompletion={handleLegacyContentCompletion}
          />
        ) : null}
        {isActive && (hasLegacyContent || isDiscussionPromptsVisible) && (
          <div className="Step__Ancillary Content--padded">
            <StepDownloads mode={mode} />
            {isDiscussionPromptsVisible && (
              <StepDiscussion
                step={step}
                canStartDiscussion={canPostDiscussionQuestion}
              />
            )}
          </div>
        )}
        {isCompletionVisible && (
          <StepCompletionContainer
            isCompletionDisabled={isCompletionDisabled}
            completionToken={completionToken}
          />
        )}
      </Box>
      {isDiscussionQuestionsVisible && isActive && (
        <StepDiscussionFeed step={step} canStartDiscussion={canPostQuestion} />
      )}
    </VStack>
  )
}

Step.displayName = "Step"

const mapStateToProps = function (state, ownProps) {
  const hasLegacyContent = state.stepStore.request.getStepHasLegacyContent()

  return _.assign(
    {},
    {
      hasLegacyContent
    },
    ownProps
  )
}

const stores = [{ store: "step" }]

export default ConnectStores(Step, stores, {}, mapStateToProps)
